import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import StepNavigation from 'shared/components/StepNavigation';
import OnboardingSplitLayout from 'features/courier-onboarding/components/OnboardingSplitLayout';
import WizardShell from 'features/courier-onboarding/components/WizardShell';
import DocOrderStep from 'features/courier-onboarding/components/DocOrderStep';
import DataStep from 'features/courier-onboarding/components/DataStep';
import DocumentsStep from 'features/courier-onboarding/components/DocumentsStep';
import PhoneVerificationPanel from 'features/courier-onboarding/components/PhoneVerificationPanel';
import ReviewStep from 'features/courier-onboarding/components/ReviewStep';
import SuccessStep from 'features/courier-onboarding/components/SuccessStep';
import { clearDraft } from 'shared/lib/storage';
import {
  CourierOnboardingProvider,
  useCourierOnboarding,
} from 'features/courier-onboarding/context/CourierOnboardingContext';
import { normalizeLang } from 'features/courier-onboarding/components/welcome/welcomeContent';
import { updateCourierLeadProgress } from 'features/courier-onboarding/services/courierOnboardingService';
import {
  isRequiredDocumentReady,
  normalizeStepKey,
  requiredDocsByTrack,
  type BusinessTrack,
  type CourierOnboardingState,
  type StepKey,
} from 'features/courier-onboarding/types';
import {
  validateLeadBasicInfo,
  validatePersonalInfo,
  validatePersonalDetails,
  validatePersonalIdentityInfo,
} from 'features/courier-onboarding/schemas';
import {
  fetchManagedCities,
  cityAreaType,
  cityAllowedVehicles,
  FALLBACK_CITIES,
  type CcCity,
} from 'features/courier-onboarding/services/lookupContentService';

const isCurrentPhoneVerified = (
  metadata: CourierOnboardingState['metadata'],
  phone: string,
): boolean =>
  metadata.phoneVerified === true &&
  metadata.verifiedPhone === phone &&
  Boolean(metadata.phoneVerificationToken);

const maskPhone = (phone: string): string => {
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 4) return '***';
  return `***-***-${digits.slice(-4)}`;
};

const isolateLtr = (value: string): string => `\u2066${value}\u2069`;

/**
 * Read a campaign-style `?city=<id>` query param exactly once on mount.
 * The id is the `cc_cities` numeric id (compared as a string) served by the
 * haat-hub management catalog.
 */
const readCityIdFromUrl = (): string | null => {
  if (typeof window === 'undefined') return null;
  const raw = new URLSearchParams(window.location.search).get('city');
  if (!raw) return null;
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const InnerPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { data, setPersonal, setMetadata } = useCourierOnboarding();

  // Campaign deep-link: if the URL carries `?city=<id>` and the id resolves
  // to a known city, prefill the name into the form and lock the picker so
  // the courier cannot switch cities away from the campaign target. We keep
  // this in local state (not the autosaved draft) so a refresh on a shared
  // device without the query param naturally unlocks the field again.
  const cityIdFromUrl = useMemo(readCityIdFromUrl, []);
  const [lockedCity, setLockedCity] = useState(false);

  // The city catalog is loaded once here so the area category (Expansion vs
  // Operation) can be derived at the intro step to drive which tracks show.
  const [cities, setCities] = useState<CcCity[]>([]);
  useEffect(() => {
    let cancelled = false;
    fetchManagedCities()
      .then((list) => {
        if (!cancelled) setCities(list.length > 0 ? list : FALLBACK_CITIES);
      })
      .catch(() => {
        if (!cancelled) setCities(FALLBACK_CITIES);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Campaign deep-link: once the catalog is loaded, resolve `?city=<id>` to a
  // known city, prefill its name and lock the picker so the courier cannot
  // switch cities away from the campaign target.
  useEffect(() => {
    if (!cityIdFromUrl || cities.length === 0) return;
    const match = cities.find((c) => String(c.id) === cityIdFromUrl);
    if (!match) return;
    setPersonal({ city: match.nameEn });
    setLockedCity(true);
  }, [cityIdFromUrl, cities, setPersonal]);

  // Area category of the currently selected city (defaults to OPERATION until
  // the catalog resolves or when a city is not matched).
  const selectedCity = cities.find(
    (c) => c.nameEn === data.personalInfo.city,
  );
  const areaType = cityAreaType(selectedCity);
  const allowedVehicles = cityAllowedVehicles(selectedCity);

  const persistedMeta = data.metadata;
  const savedStep = normalizeStepKey(persistedMeta.currentStep) ?? 'intro';
  const savedPhoneVerified = isCurrentPhoneVerified(
    persistedMeta,
    data.personalInfo.phoneNumber,
  );
  const [step, setStep] = useState<StepKey>(
    savedStep === 'review' && !savedPhoneVerified ? 'personal' : savedStep,
  );
  const [showOtp, setShowOtp] = useState(
    savedStep === 'review' && !savedPhoneVerified,
  );
  useEffect(() => {
    if (step === 'intro' && !cityIdFromUrl) {
      setPersonal({ city: '' });
    }
  }, [cityIdFromUrl, setPersonal, step]);

  // The onboarding flow is always upload-first: the courier picks a track and
  // immediately moves to the documents step so the AI can prefill the form.
  const [trackSelection, setTrackSelection] = useState<BusinessTrack | null>(
    persistedMeta.businessTrack,
  );
  const [docsReady, setDocsReady] = useState(false);

  // Expansion areas cancel the finance-company track: if it was selected before
  // switching to an expansion city, fall back to the licensed-business track.
  useEffect(() => {
    if (areaType === 'EXPANSION' && trackSelection === 'FINANCIAL_COMPANY') {
      setTrackSelection('LICENSED_BUSINESS');
    }
  }, [areaType, trackSelection]);

  const order: StepKey[] = useMemo(
    () => ['intro', 'documents', 'personal', 'review', 'success'],
    [],
  );

  const trackedOrder: StepKey[] = useMemo(
    () => ['documents', 'personal', 'review'],
    [],
  );

  const currentIdx = order.indexOf(step);
  const trackedIdx = trackedOrder.indexOf(step);
  const totalTracked = trackedOrder.length;
  const currentTracked =
    step === 'success' ? totalTracked : trackedIdx >= 0 ? trackedIdx + 1 : 1;

  // Clamped because drafts saved on the previous 5-step flow can carry a
  // frontier beyond the number of steps that now exist.
  const [maxReachedTracked, setMaxReachedTracked] = useState(() =>
    Math.min(persistedMeta.maxReachedTracked ?? 1, trackedOrder.length),
  );
  useEffect(() => {
    if (trackedIdx >= 0) {
      setMaxReachedTracked((prev) => Math.max(prev, trackedIdx + 1));
    } else if (step === 'success') {
      setMaxReachedTracked(totalTracked);
    }
  }, [step, trackedIdx, totalTracked]);

  // Persist wizard navigation back into the autosaved draft so a refresh
  // resumes on the same step instead of bouncing the user to the start.
  useEffect(() => {
    setMetadata({ currentStep: step, maxReachedTracked });
  }, [step, maxReachedTracked, setMetadata]);

  // Once the user has successfully submitted, wipe the draft so the next
  // visit starts fresh instead of immediately landing on the success screen.
  useEffect(() => {
    if (step === 'success') {
      clearDraft();
    }
  }, [step]);

  const go = (next: StepKey) => setStep(next);
  const goPrev = () => {
    if (currentIdx > 0) setStep(order[currentIdx - 1]);
  };

  const handleOtpVerified = async (token: string) => {
    const phone = data.personalInfo.phoneNumber;
    setMetadata({
      phoneVerified: true,
      verifiedPhone: phone,
      phoneVerificationToken: token,
      otpChallengeId: undefined,
      otpSentTo: undefined,
    });
    setShowOtp(false);

    const leadId = data.metadata.leadId;
    if (leadId) {
      const {
        phoneVerificationToken: _token,
        otpChallengeId: _challenge,
        otpSentTo: _sentTo,
        ...rest
      } = data.metadata;
      const leadMetadata = {
        ...rest,
        phoneVerified: true,
        verifiedPhone: phone,
      };
      try {
        await updateCourierLeadProgress(leadId, {
          lastCompletedStep: 'personal',
          currentStep: 'review',
          nextStep: 'review',
          draftData: {
            personalInfo: data.personalInfo,
            vehicleInfo: data.vehicleInfo,
            experienceInfo: data.experienceInfo,
            metadata: leadMetadata,
          },
          metadata: leadMetadata,
        });
      } catch {
        // The local token still lets the courier submit.
      }
    }

    go('review');
  };
  const goNext = () => {
    if (currentIdx < order.length - 1) setStep(order[currentIdx + 1]);
  };

  // Whether the wizard step the user is currently on is internally complete.
  // Used to gate stepper-initiated jumps so the courier cannot navigate
  // away from an invalid state (e.g. deleting a required document on the
  // documents step and then trying to jump ahead to the data step).
  const isStepComplete = (target: StepKey): boolean => {
    switch (target) {
      case 'personal': {
        if (
          Object.keys(validateLeadBasicInfo(data.personalInfo)).length !== 0 ||
          Object.keys(validatePersonalIdentityInfo(data.personalInfo))
            .length !== 0 ||
          Object.keys(validatePersonalDetails(data.personalInfo)).length !== 0
        )
          return false;
        if (data.vehicleInfo.vehicleTypes.length === 0) return false;
        if (data.experienceInfo.hasDeliveryExperience === null) return false;
        if (
          data.experienceInfo.hasDeliveryExperience &&
          !data.experienceInfo.yearsOfExperienceRange
        )
          return false;
        return true;
      }
      case 'review':
        return Object.keys(validatePersonalInfo(data.personalInfo)).length === 0;
      case 'documents': {
        const track = data.metadata.businessTrack;
        if (!track) return false;
        const required = requiredDocsByTrack[track];
        return required.every((k) =>
          isRequiredDocumentReady(k, data.documents),
        );
      }
      default:
        return true;
    }
  };

  // Stepper-initiated jumps. PremiumStepper only invokes this for already-
  // reached steps that are not the active one, so we only need to map the
  // 1-based tracked step number back to a tracked key. We additionally block
  // forward jumps when the user's current step still has invalid/missing
  // data — backward jumps to earlier steps stay allowed so they can fix it.
  const handleStepSelect = (stepNumber: number) => {
    const idx = stepNumber - 1;
    const target = trackedOrder[idx];
    if (!target) return;
    const currentTrackIdx = trackedOrder.indexOf(step);
    const targetIdx = idx;
    const isForward = currentTrackIdx >= 0 && targetIdx > currentTrackIdx;
    if (isForward && !isStepComplete(step)) return;
    if (
      target === 'review' &&
      !isCurrentPhoneVerified(data.metadata, data.personalInfo.phoneNumber)
    ) {
      return;
    }
    setShowOtp(false);
    setStep(target);
  };

  if (step === 'intro') {
    const handleContinue = () => {
      if (trackSelection === null || !data.personalInfo.city) return;
      setMetadata({
        documentsFirst: true,
        businessTrack: trackSelection,
        areaType,
      });
      setStep('documents');
    };

    const introHeader = (
      <header className="mb-6 sm:mb-8 text-center sm:text-start">
        <motion.span
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="inline-flex items-center gap-2 rounded-full border border-[#bf0035]/10 bg-[#bf0035]/[0.06] px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-[#bf0035]"
        >
          <Sparkles size={13} />
          {t('onboarding.docOrder.eyebrow')}
        </motion.span>
        <motion.h1
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
          className="mt-3 text-2xl sm:text-[28px] font-extrabold text-gray-900 tracking-tight leading-[1.2]"
        >
          {t('onboarding.docOrder.title')}
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.14, ease: [0.22, 1, 0.36, 1] }}
          className="mt-2 text-sm sm:text-[15px] text-gray-600 leading-relaxed max-w-2xl"
        >
          {t('onboarding.docOrder.description')}
        </motion.p>
      </header>
    );

    return (
      <OnboardingSplitLayout
        footer={
          <StepNavigation
            compact
            hideBack
            onNext={handleContinue}
            nextDisabled={
              trackSelection === null || !docsReady || !data.personalInfo.city
            }
            nextLabel={t('common.continue') ?? undefined}
          />
        }
      >
        {introHeader}
        <DocOrderStep
          track={trackSelection}
          onTrackChange={setTrackSelection}
          docsReady={docsReady}
          onDocsReadyChange={setDocsReady}
          cities={cities}
          cityValue={data.personalInfo.city}
          onCityChange={(value) => setPersonal({ city: value })}
          cityLocked={lockedCity}
          areaType={areaType}
        />
      </OnboardingSplitLayout>
    );
  }

  const renderStep = () => {
    switch (step) {
      case 'personal':
        if (showOtp) {
          const masked =
            data.metadata.otpSentTo || maskPhone(data.personalInfo.phoneNumber);
          return (
            <PhoneVerificationPanel
              phone={data.personalInfo.phoneNumber}
              language={normalizeLang(i18n.language)}
              challengeId={data.metadata.otpChallengeId ?? null}
              sentTo={masked}
              onChallenge={(challengeId, sentTo) =>
                setMetadata({ otpChallengeId: challengeId, otpSentTo: sentTo })
              }
              onVerified={(token) => {
                void handleOtpVerified(token);
              }}
              onChangePhone={() => setShowOtp(false)}
            />
          );
        }
        return (
          <DataStep
            onReadyForOtp={() => setShowOtp(true)}
            onBack={goPrev}
            lockedCity
            allowedVehicles={allowedVehicles}
          />
        );
      case 'documents':
        return (
          <DocumentsStep
            onValid={goNext}
            onBack={goPrev}
            nextLabel={t('common.next') ?? 'Next'}
          />
        );
      case 'review':
        return (
          <ReviewStep
            onSubmitted={() => go('success')}
            onBack={goPrev}
            onEdit={go}
          />
        );
      case 'success':
        return <SuccessStep />;
      default:
        return null;
    }
  };

  const titleByStep: Record<StepKey, string> = {
    welcome: '',
    intro: t('onboarding.docOrder.title'),
    personal: t('onboarding.personal.title'),
    documents: t('onboarding.documents.title'),
    review: t('onboarding.review.title'),
    success: '',
  };

  const subtitleByStep: Record<StepKey, string | undefined> = {
    welcome: undefined,
    intro: t('onboarding.docOrder.description') ?? undefined,
    personal: t('onboarding.personal.subtitle') ?? undefined,
    documents: t('onboarding.documents.subtitle') ?? undefined,
    review: t('onboarding.review.subtitle') ?? undefined,
    success: undefined,
  };

  // When the current step is incomplete (e.g. a required document was just
  // deleted), collapse the visible "reached" frontier back to the current
  // step so the future numbered circles re-lock — gives clear visual feedback
  // that the courier needs to fix this step before moving on.
  const effectiveMaxReached =
    trackedIdx >= 0 && !isStepComplete(step)
      ? Math.min(maxReachedTracked, trackedIdx + 1)
      : maxReachedTracked;

  const otpActive = showOtp && step === 'personal';
  const maskedPhone =
    data.metadata.otpSentTo || maskPhone(data.personalInfo.phoneNumber);

  return (
    <WizardShell
      title={otpActive ? t('onboarding.otp.title') : titleByStep[step]}
      subtitle={
        otpActive
          ? (t('onboarding.otp.subtitle', {
              phone: isolateLtr(maskedPhone),
            }) as string)
          : subtitleByStep[step]
      }
      currentStep={currentTracked}
      totalSteps={totalTracked}
      stepKeys={step === 'success' ? undefined : trackedOrder}
      maxReached={effectiveMaxReached}
      onStepSelect={handleStepSelect}
    >
      {renderStep()}
    </WizardShell>
  );
};

const CourierOnboardingPage: React.FC = () => (
  <CourierOnboardingProvider>
    <InnerPage />
  </CourierOnboardingProvider>
);

export default CourierOnboardingPage;
